import { useCallback, useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import type {
  PcmpAccountsBatchProposalsParams,
  PcmpAccountsBatchSearch,
  PcmpAccountsBatchView,
  PcmpRedisburseBatchProposalsParams,
  PcmpRedisburseBatchSearch,
} from '../types/pcmpAccounts.types';
import { usePcmpQueueFilters } from './usePcmpQueueFilters';

const batchRoute = getRouteApi('/_auth/pcmp/accounts/batches/$batchNo/');
const redisburseRoute = getRouteApi('/_auth/pcmp/accounts/redisburse-batches/$batchNo/');

/** A batch's members: the queue filters, paging and "show all members". */
function useBatchMemberFilters<S extends PcmpAccountsBatchSearch>(
  batchNo: number,
  search: S,
  setSearch: (update: (prev: S) => S) => void,
) {
  const queue = usePcmpQueueFilters(search, setSearch);
  const { appliedFilters, page, limit } = queue;
  const showAll = search.showAll ?? false;

  const params = useMemo<PcmpAccountsBatchProposalsParams>(
    () => ({ ...appliedFilters, page, limit, showAll: showAll || undefined }),
    [appliedFilters, page, limit, showAll],
  );

  const setShowAll = useCallback(
    (next: boolean) => {
      setSearch((prev) => ({ ...prev, showAll: next || undefined, page: undefined }));
    },
    [setSearch],
  );

  return { ...queue, batchNo, showAll, params, setShowAll };
}

export type PcmpAccountsBatchFilterController = ReturnType<
  typeof useBatchMemberFilters<PcmpAccountsBatchSearch>
>;

/** A route or urgent batch's members. */
export function usePcmpAccountsBatchFilters() {
  const { batchNo } = batchRoute.useParams();
  const search = batchRoute.useSearch();
  const navigate = batchRoute.useNavigate();
  const setSearch = useCallback(
    (update: (prev: PcmpAccountsBatchSearch) => PcmpAccountsBatchSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );
  return useBatchMemberFilters(batchNo, search, setSearch);
}

/** A re-disbursement batch's members, all or one view (still to pay, paid). */
export function usePcmpRedisburseBatchFilters() {
  const { batchNo } = redisburseRoute.useParams();
  const search = redisburseRoute.useSearch();
  const navigate = redisburseRoute.useNavigate();
  const setSearch = useCallback(
    (update: (prev: PcmpRedisburseBatchSearch) => PcmpRedisburseBatchSearch) => {
      void navigate({ search: update });
    },
    [navigate],
  );
  const members = useBatchMemberFilters(batchNo, search, setSearch);
  const { view } = search;

  const params = useMemo<PcmpRedisburseBatchProposalsParams>(
    () => ({ ...members.params, view }),
    [members.params, view],
  );

  const setView = useCallback(
    (next: PcmpAccountsBatchView | undefined) => {
      setSearch((prev) => ({ ...prev, view: next, page: undefined }));
    },
    [setSearch],
  );

  return { ...members, view, params, setView };
}
